import { Box, MenuItem, styled, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ProductDto } from "../../services/generated.schemas";
import CustomSelect from "../general/CustomSelect";
import { destructVariantsColors, destructVariantsSizes } from "../utils";

type SelectedProductProps = {
  selectedProduct: ProductDto | undefined;
  setSelectedVariant: Dispatch<SetStateAction<number>>;
};

const Wrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  marginTop: "clamp(1.5rem, 4vw, 3rem)",
});

const Row = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "clamp(8px, 2vw, 16px)",
  flexWrap: "wrap"
});

const RowWithMargin = styled(Row)({
  marginTop: "clamp(0.5rem, 2vw, 1rem)",
});

const Label = styled(Typography)({
  marginRight: "1rem",
  textTransform: "uppercase",
  letterSpacing: 1,
  minWidth: "clamp(50px, 15%, 80px)", 
  fontSize: "clamp(0.7rem, 1.5vw, 1rem)",
});

const PhotoAdjustments = ({
  selectedProduct,
  setSelectedVariant,
}: SelectedProductProps) => {
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedFrameColor, setSelectedFrameColor] = useState("");

  const sizes = destructVariantsSizes(selectedProduct?.variants);
  const colors = destructVariantsColors(selectedProduct?.variants);

  useEffect(() => {
    setSelectedSize(sizes[0]);
    setSelectedFrameColor(colors[0]);
  }, [selectedProduct]);

  useEffect(() => {
    if (!selectedProduct) return;
    const variantTitle = `${selectedSize}/${selectedFrameColor}`;
    const index = selectedProduct.variants.findIndex(
      (variant) => variant.title === variantTitle,
    );
    setSelectedVariant(index >= 0 ? index : 0);
  }, [selectedProduct, selectedSize, selectedFrameColor, setSelectedVariant]);

  const handleSizePick = (size: string) => {
    setSelectedSize(size);
  };

  const handleFrameColorPick = (frameColor: string) => {
    setSelectedFrameColor(frameColor);
  };

  return (
    <Wrapper>
      <Row>
        <Label variant="body1">Size</Label>
        <CustomSelect
          value={selectedSize}
          onChange={(event: any) => handleSizePick(event.target.value)}
          width="clamp(80px, 40%, 200px)"
          height="clamp(20px, 5vh, 45px)"
          displayEmpty
        >
          {sizes.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </CustomSelect>
      </Row>
      {selectedFrameColor && <RowWithMargin>
        <Label variant="body1">Frame</Label>
        <CustomSelect
          value={selectedFrameColor}
          onChange={(event: any) => handleFrameColorPick(event.target.value)}
          width="clamp(80px, 40%, 200px)"
          height="clamp(20px, 5vh, 45px)"
          displayEmpty
        >
          {colors.map((frameColor) => (
            <MenuItem key={frameColor} value={frameColor}>
              {frameColor}
            </MenuItem>
          ))}
        </CustomSelect>
      </RowWithMargin>}
    </Wrapper>
  );
};

export default PhotoAdjustments;
